import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import ConfirmModal from '../shared/ConfirmModal';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_MAP = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6
};

// US Federal Holidays
const getNthWeekday = (year, month, weekday, n) => {
  const first = new Date(year, month, 1);
  const diff = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + diff + (n - 1) * 7);
};

const getLastWeekday = (year, month, weekday) => {
  const last = new Date(year, month + 1, 0);
  const diff = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - diff);
};

const getUSHolidays = (year) => [
  { date: new Date(year, 0, 1), name: "New Year's Day" },
  { date: getNthWeekday(year, 0, 1, 3), name: 'MLK Day' },
  { date: getNthWeekday(year, 1, 1, 3), name: "Presidents' Day" },
  { date: getLastWeekday(year, 4, 1), name: 'Memorial Day' },
  { date: new Date(year, 5, 19), name: 'Juneteenth' },
  { date: new Date(year, 6, 4), name: 'Independence Day' },
  { date: getNthWeekday(year, 8, 1, 1), name: 'Labor Day' },
  { date: getNthWeekday(year, 9, 1, 2), name: 'Columbus Day' },
  { date: new Date(year, 10, 11), name: "Veterans Day" },
  { date: getNthWeekday(year, 10, 4, 4), name: 'Thanksgiving' },
  { date: new Date(year, 11, 25), name: 'Christmas Day' },
];

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const CalendarPage = () => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [courses, setCourses] = useState([]);
  const [studioHolidays, setStudioHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, studioRes] = await Promise.all([
          api.get('/courses?status=active'),
          api.get('/studio')
        ]);
        setCourses(coursesRes.data.courses);
        setStudioHolidays(studioRes.data.studio.holidays || []);
      } catch (err) {
        console.error('Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const prevMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () =>
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  // Build events map for the visible calendar grid
  const buildEvents = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const events = {};

    const addEvent = (date, event) => {
      const key = dateKey(date);
      if (!events[key]) events[key] = [];
      events[key].push(event);
    };

    // US Holidays — current year (plus adjacent if month is Jan or Dec)
    const years = [year];
    if (month === 0) years.push(year - 1);
    if (month === 11) years.push(year + 1);
    years.forEach(y => {
      getUSHolidays(y).forEach(h =>
        addEvent(h.date, { type: 'us_holiday', name: h.name })
      );
    });

    // Studio holidays
    studioHolidays.forEach(h => {
      const d = new Date(h.date);
      addEvent(d, { type: 'studio_holiday', name: h.name, id: h._id });
    });

    // Course class sessions
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const gridStart = new Date(year, month, 1 - firstDay);
    const gridEnd = new Date(year, month, daysInMonth + (42 - daysInMonth - firstDay));

    courses.forEach(course => {
      if (course.type === 'long_course' && course.schedule?.days?.length > 0) {
        const courseDays = course.schedule.days.map(d => DAY_MAP[d]);
        const d = new Date(gridStart);
        while (d <= gridEnd) {
          if (courseDays.includes(d.getDay())) {
            addEvent(new Date(d), {
              type: 'class',
              name: course.name,
              time: course.schedule.startTime || ''
            });
          }
          d.setDate(d.getDate() + 1);
        }
      } else if (course.type === 'block' && course.schedule?.blockStartDate) {
        const blockStart = new Date(course.schedule.blockStartDate);
        const blockWeeks = course.schedule.blockWeeks || 8;
        const blockEnd = new Date(blockStart);
        blockEnd.setDate(blockEnd.getDate() + blockWeeks * 7);
        const courseDays = (course.schedule.days || []).map(d => DAY_MAP[d]);

        const d = new Date(Math.max(blockStart.getTime(), gridStart.getTime()));
        const end = new Date(Math.min(blockEnd.getTime(), gridEnd.getTime()));
        while (d <= end) {
          if (courseDays.includes(d.getDay())) {
            addEvent(new Date(d), {
              type: 'block',
              name: course.name,
              time: course.schedule.startTime || ''
            });
          }
          d.setDate(d.getDate() + 1);
        }
      } else if (course.type === 'workshop' && course.schedule?.sessionDate) {
        const d = new Date(course.schedule.sessionDate);
        addEvent(d, {
          type: 'workshop',
          name: course.name,
          time: course.schedule.sessionStartTime || ''
        });
      }
    });

    return events;
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/studio/holidays', newHoliday);
      setStudioHolidays(data.holidays);
      setShowAddHoliday(false);
      setNewHoliday({ date: '', name: '' });
    } catch (err) {
      console.error('Failed to add holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      const { data } = await api.delete(`/studio/holidays/${id}`);
      setStudioHolidays(data.holidays);
      setDeleteConfirm({ open: false, id: null, name: '' });
    } catch (err) {
      console.error('Failed to delete holiday');
    }
  };

  if (loading) return <div className="text-gray-500">Loading calendar...</div>;

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const events = buildEvents();
  const today = new Date();
  const todayKey = dateKey(today);

  // Build 42-cell grid
  const cells = [];
  const startDate = new Date(year, month, 1 - firstDay);
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    cells.push(d);
  }

  // Check if we need 6 rows or 5 is enough
  const lastCellMonth = cells[34].getMonth();
  const needsSixRows = lastCellMonth === month || cells[35].getDate() <= daysInMonth;
  const visibleCells = needsSixRows ? cells : cells.slice(0, 35);

  const selectedDayEvents = selectedDay ? (events[selectedDay] || []) : [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
        <button
          onClick={() => setShowAddHoliday(true)}
          className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
        >
          + Studio Holiday
        </button>
      </div>

      {/* Calendar card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Month nav */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button
            onClick={prevMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-600 text-xl"
          >
            &#8249;
          </button>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {MONTH_NAMES[month]} {year}
            </h2>
            <button
              onClick={goToday}
              className="text-xs text-brand-500 hover:text-brand-600 font-medium px-2 py-1 rounded hover:bg-brand-50 transition-colors"
            >
              Today
            </button>
          </div>
          <button
            onClick={nextMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-600 text-xl"
          >
            &#8250;
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
          {DAY_NAMES.map(d => (
            <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {visibleCells.map((date, i) => {
            const key = dateKey(date);
            const isCurrentMonth = date.getMonth() === month;
            const isToday = key === todayKey;
            const dayEvents = events[key] || [];
            const isSelected = selectedDay === key;

            return (
              <div
                key={i}
                onClick={() => setSelectedDay(isSelected ? null : key)}
                className={`min-h-[90px] border-b border-r border-gray-50 p-1.5 cursor-pointer transition-colors ${
                  !isCurrentMonth ? 'bg-gray-50/50' : ''
                } ${isToday ? 'bg-brand-50/30' : ''} ${
                  isSelected ? 'ring-2 ring-inset ring-brand-400' : ''
                } hover:bg-gray-50/80`}
              >
                <div className={`text-sm mb-1 ${
                  isToday
                    ? 'font-bold'
                    : !isCurrentMonth
                    ? 'text-gray-300'
                    : 'text-gray-700 font-medium'
                }`}>
                  {isToday ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-bold">
                      {date.getDate()}
                    </span>
                  ) : (
                    date.getDate()
                  )}
                </div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((event, j) => (
                    <div
                      key={j}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate ${
                        event.type === 'us_holiday'
                          ? 'bg-red-50 text-red-600'
                          : event.type === 'studio_holiday'
                          ? 'bg-orange-50 text-orange-700 font-medium'
                          : event.type === 'workshop'
                          ? 'bg-purple-50 text-purple-600'
                          : event.type === 'block'
                          ? 'bg-teal-50 text-teal-600'
                          : 'bg-brand-50 text-brand-600'
                      }`}
                      title={event.name}
                    >
                      {event.name}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-gray-400 px-1">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-brand-100 inline-block" /> Long Courses
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-teal-100 inline-block" /> Block Courses
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-purple-100 inline-block" /> Workshops
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-red-100 inline-block" /> US Holidays
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-orange-100 inline-block" /> Studio Holidays
        </span>
      </div>

      {/* Day detail panel */}
      {selectedDay && selectedDayEvents.length > 0 && (
        <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
            })}
          </h3>
          <div className="space-y-2">
            {selectedDayEvents.map((event, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  event.type === 'us_holiday' ? 'bg-red-500'
                    : event.type === 'studio_holiday' ? 'bg-orange-500'
                    : event.type === 'workshop' ? 'bg-purple-500'
                    : event.type === 'block' ? 'bg-teal-500'
                    : 'bg-brand-500'
                }`} />
                <span className="text-sm text-gray-900">{event.name}</span>
                {event.time && (
                  <span className="text-xs text-gray-500">{event.time}</span>
                )}
                {event.type === 'studio_holiday' && event.id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm({ open: true, id: event.id, name: event.name });
                    }}
                    className="text-xs text-red-500 hover:text-red-700 ml-auto"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Studio Holidays list */}
      {studioHolidays.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Studio Holidays</h3>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
            {[...studioHolidays]
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map(h => (
                <div key={h._id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{h.name}</span>
                    <span className="text-sm text-gray-500 ml-3">
                      {new Date(h.date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </span>
                  </div>
                  <button
                    onClick={() => setDeleteConfirm({ open: true, id: h._id, name: h.name })}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Add Holiday Modal */}
      {showAddHoliday && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddHoliday(false)} />
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Studio Holiday</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name</label>
                <input
                  type="text"
                  value={newHoliday.name}
                  onChange={e => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter holiday name"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newHoliday.date}
                  onChange={e => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowAddHoliday(false); setNewHoliday({ date: '', name: '' }); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddHoliday}
                disabled={saving || !newHoliday.date || !newHoliday.name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
              >
                {saving ? 'Adding...' : 'Add Holiday'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Holiday"
        message={`Remove "${deleteConfirm.name}" from your studio holidays?`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => handleDeleteHoliday(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
      />
    </div>
  );
};

export default CalendarPage;
